/**
 * supabase-js (@supabase/auth-js) 内部の `console.error` を PostHog の
 * `$exception` 自動起票から除外するためのフィルター (#174 / #177)。
 *
 * 背景:
 * - `AuthRetryableFetchError` — auth-js の `_emitInitialSession()` /
 *   `_autoRefreshTokenTick()` が一時的なネットワーク失敗を console.error で
 *   出力する。ライブラリ内部でリトライされるため機能影響はない (#174)。
 * - `AuthApiError: Invalid Refresh Token` — トークンローテーション競合
 *   (複数タブ / 連続リロード) や長期放置後のアクセスで発生。auth-js が
 *   セッションを破棄して SIGNED_OUT を emit し、useSessionErrorHandler が
 *   ログイン画面へ誘導するため回復可能 (#177)。
 *
 * どちらもアプリコードから直接 catch できないライブラリ内部の出力のため、
 * console.error をラップして warn へ格下げする。それ以外のエラーは素通し。
 *
 * 追加 (#195):
 * - 一過性ネットワークエラー (TypeError: Load failed 等) — React 19 は Error
 *   Boundary が捕捉したエラーを production でも無条件に console.error へ出力
 *   するため、error.tsx 側の warn 格下げより先に PostHog へ記録されてしまう。
 *   isTransientNetworkError() に該当するものも同様に warn へ格下げする。
 */

import { isTransientNetworkError } from "@/lib/errors/transient";

let installed = false;

function isSuppressibleAuthError(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const name = (value as { name?: unknown }).name;
  const message = (value as { message?: unknown }).message;
  if (name === "AuthRetryableFetchError") return true;
  if (
    name === "AuthApiError" &&
    typeof message === "string" &&
    /refresh token/i.test(message)
  ) {
    return true;
  }
  return false;
}

function isSuppressible(value: unknown): boolean {
  return isSuppressibleAuthError(value) || isTransientNetworkError(value);
}

export function suppressAuthConsoleNoise(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const original = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    if (args.some(isSuppressible)) {
      console.warn("[transient / auth]", ...args);
      return;
    }
    original(...args);
  };
}
